import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronDown, Copy, Ellipsis, Folder, ListTodo, Pencil, Plus, Star, Trash2, Layers } from '@/components/ui/icons';
import type { Location, View, ViewType } from '@shared/types';
import { VIEW_TYPE_META } from '@shared/constants';
import { getDb, useDb, useMe } from '@/lib/store';
import { breadcrumb, locationName } from '@/lib/hierarchy';
import { locationPath, useWorkspaceId } from '@/lib/nav';
import { createView, deleteView, duplicateView, ensureSpaceOverview, toggleFavorite } from '@/lib/actions';
import { pushRecent } from '@/lib/ui';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuSeparator } from '@/components/ui/Menu';
import { Icon } from '@/components/ui/Icon';
import { openContextMenu } from '@/components/ui/overlays';
import { SpaceAvatar } from '@/shell/WorkspaceLogo';
import { openDialog } from '@/shell/dialogs';
import { ListView } from '@/views/ListView';
import { BoardView } from '@/views/BoardView';
import { CalendarView } from '@/views/CalendarView';
import { TableView } from '@/views/TableView';
import { GanttView } from '@/views/GanttView';
import { OverviewView } from '@/views/OverviewView';
import { ShareButton } from '@/components/ShareButton';

function ViewTypeIcon({ type, size = 14 }: { type: ViewType; size?: number }) {
  const meta = VIEW_TYPE_META[type];
  return (
    <span className="view-type-icon" style={{ background: meta.color, width: size + 2, height: size + 2 }}>
      <Icon name={meta.icon} size={size - 4} color="#fff" strokeWidth={2.4} />
    </span>
  );
}

function Breadcrumb({ location }: { location: Location }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const db = useDb((d) => d);
  const crumbs = breadcrumb(db, location);
  const isFav = Object.values(db.favorites).some((f) => f.userId === me && f.target.type === location.type && f.target.id === location.id);
  if (location.type === 'workspace') {
    return (
      <div className="loc-crumbs">
        <span className="loc-crumb current">
          <Layers size={15} className="faint" /> All Tasks
        </span>
      </div>
    );
  }
  return (
    <div className="loc-crumbs">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        const icon = c.type === 'space' ? <SpaceAvatar spaceId={c.id} size={16} /> : c.type === 'folder' ? <Folder size={15} className="faint" /> : <ListTodo size={15} className="faint" />;
        return (
          <span key={c.id} className="row">
            {i > 0 && <span className="loc-sep">/</span>}
            {last ? (
              <Popover
                content={(close) => (
                  <Menu>
                    <MenuItem icon={<Pencil size={15} />} label="Rename" onClick={() => (close(), openDialog({ kind: 'rename', c: c.type === 'space' ? 'spaces' : c.type === 'folder' ? 'folders' : 'lists', id: c.id, name: locationName(db, c) }))} />
                    <MenuItem icon={<Star size={15} />} label={isFav ? 'Remove from favorites' : 'Add to favorites'} onClick={() => (close(), toggleFavorite(c as never))} />
                    <MenuItem icon={<Layers size={15} />} label="Task statuses" onClick={() => (close(), openDialog({ kind: 'statuses', target: c as never }))} />
                  </Menu>
                )}
              >
                <button className="loc-crumb current">
                  {icon}
                  <span className="truncate">{locationName(db, c)}</span>
                  <ChevronDown size={14} className="faint" />
                </button>
              </Popover>
            ) : (
              <button className="loc-crumb" onClick={() => navigate(locationPath(ws, c))}>
                {icon}
                <span className="truncate">{locationName(db, c)}</span>
              </button>
            )}
          </span>
        );
      })}
      <button className={clsx('icon-btn sm', { 'fav-on': isFav })} onClick={() => toggleFavorite(location as never)} title="Favorite">
        <Star size={15} fill={isFav ? '#f8ae00' : 'none'} color={isFav ? '#f8ae00' : 'currentColor'} />
      </button>
    </div>
  );
}

function ViewTabs({ location, views, activeId }: { location: Location; views: View[]; activeId: string | undefined }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const go = (id: string) => navigate(locationPath(ws, location, id));
  const viewMenu = (v: View, close: () => void) => (
    <Menu>
      <MenuItem icon={<Pencil size={15} />} label="Rename" onClick={() => (close(), openDialog({ kind: 'rename', c: 'views', id: v.id, name: v.name }))} />
      <MenuItem
        icon={<Copy size={15} />}
        label="Duplicate view"
        onClick={() => {
          close();
          const c = duplicateView(v.id);
          if (c) go(c.id);
        }}
      />
      <MenuSeparator />
      <MenuItem
        icon={<Trash2 size={15} />}
        label="Delete view"
        danger
        disabled={views.length <= 1}
        onClick={() => {
          close();
          deleteView(v.id);
          const next = views.find((x) => x.id !== v.id);
          if (next) go(next.id);
        }}
      />
    </Menu>
  );
  return (
    <div className="view-tabs">
      {views.map((v) => (
        <button
          key={v.id}
          className={clsx('view-tab', { active: v.id === activeId })}
          onClick={() => go(v.id)}
          onContextMenu={(e) => openContextMenu(e, (close) => viewMenu(v, close))}
        >
          <ViewTypeIcon type={v.type} />
          <span>{v.name}</span>
          {v.id === activeId && (
            <Popover content={(close) => viewMenu(v, close)}>
              <span className="view-tab-more" role="button">
                <Ellipsis size={14} />
              </span>
            </Popover>
          )}
        </button>
      ))}
      <Popover
        content={(close) => (
          <Menu style={{ width: 300 }}>
            <div className="menu-label">Add view</div>
            {((location.type === 'space' || location.type === 'folder' ? ['overview', 'list', 'board', 'calendar', 'table', 'gantt'] : ['list', 'board', 'calendar', 'table', 'gantt']) as ViewType[]).map((t) => (
              <MenuItem
                key={t}
                icon={<ViewTypeIcon type={t} size={16} />}
                label={VIEW_TYPE_META[t].label}
                hint={VIEW_TYPE_META[t].description}
                onClick={() => {
                  close();
                  const v = createView(location, t);
                  go(v.id);
                }}
              />
            ))}
          </Menu>
        )}
      >
        <button className="view-tab add">
          <Plus size={14} />
          <span>View</span>
        </button>
      </Popover>
    </div>
  );
}

export function LocationPage({ location, viewId }: { location: Location; viewId?: string }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const exists = useDb((db) =>
    location.type === 'workspace' ? !!db.workspaces[location.id] : location.type === 'space' ? !!db.spaces[location.id] : location.type === 'folder' ? !!db.folders[location.id] : !!db.lists[location.id],
  );
  const allViews = useDb((db) => db.views);
  const views = useMemo(
    () => Object.values(allViews).filter((v) => v.location.type === location.type && v.location.id === location.id).sort((a, b) => a.orderIndex - b.orderIndex),
    [allViews, location.type, location.id],
  );
  const [lastView, setLastView] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem('atrium.lastView') ?? '{}');
    } catch {
      return {};
    }
  });
  const key = `${location.type}:${location.id}`;
  const active = views.find((v) => v.id === viewId) ?? views.find((v) => v.id === lastView[key]) ?? views[0];

  useEffect(() => {
    if (!exists) return;
    pushRecent({ kind: 'location', id: location.id, location: { type: location.type, id: location.id } });
  }, [exists, location.type, location.id]);

  useEffect(() => {
    if (!active) return;
    const next = { ...lastView, [key]: active.id };
    setLastView(next);
    try {
      localStorage.setItem('atrium.lastView', JSON.stringify(next));
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, key]);

  // locations without any view get a default List view; spaces also open on an Overview (first tab, added once
  // per space), which becomes the remembered view when it's added
  useEffect(() => {
    if (!exists) return;
    const ov = location.type === 'space' ? ensureSpaceOverview(location.id) : null;
    if (ov) {
      setLastView((prev) => {
        const next = { ...prev, [key]: ov.id };
        try {
          localStorage.setItem('atrium.lastView', JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    }
    if (!Object.values(getDb().views).some((v) => v.location.type === location.type && v.location.id === location.id && v.type !== 'overview'))
      createView({ type: location.type, id: location.id }, 'list');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exists, views.length, location.type, location.id]);

  if (!exists) {
    return (
      <div className="page-placeholder">
        <h2>This location doesn't exist</h2>
        <button className="btn outline" onClick={() => navigate(`/${ws}/home`)}>
          Go home
        </button>
      </div>
    );
  }

  return (
    <div className="loc-page">
      <div className="loc-header">
        <div className="loc-header-top">
          <Breadcrumb location={location} />
          <span className="spacer" />
          <ShareButton target={location} link={() => window.location.href} />
        </div>
        <ViewTabs location={location} views={views} activeId={active?.id} />
      </div>
      {active && <ViewBody key={active.id} view={active} location={location} />}
    </div>
  );
}

function ViewBody({ view, location }: { view: View; location: Location }) {
  switch (view.type) {
    case 'board':
      return <BoardView view={view} location={location} />;
    case 'calendar':
      return <CalendarView view={view} location={location} />;
    case 'table':
      return <TableView view={view} location={location} />;
    case 'gantt':
      return <GanttView view={view} location={location} />;
    case 'overview':
      return <OverviewView location={location} />;
    default:
      return <ListView view={view} location={location} />;
  }
}
